import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectRoles } from "./features/roleSlice";
import {
  NAV_SECTIONS,
  sectionGroups,
  checkRoles,
  matchesQuery,
} from "./components/Navigation/SideNav";

import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DoorBackIcon from "@mui/icons-material/DoorBack";
import AssignmentIcon from "@mui/icons-material/Assignment";
import KitchenIcon from "@mui/icons-material/Kitchen";
import FoundationIcon from "@mui/icons-material/Foundation";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AppsIcon from "@mui/icons-material/Apps";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import EditNoteIcon from "@mui/icons-material/EditNote";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";

// Icons stay out of the data file so the nav model remains plain data the
// favorites catalog can import without pulling in MUI.
const SECTION_ICONS = {
  forms: EditNoteIcon,
  "windows-doors": DoorBackIcon,
  sales: AssignmentIcon,
  cabinets: KitchenIcon,
  sbcf: FoundationIcon,
  fleet: LocalShippingIcon,
  safety: HealthAndSafetyIcon,
  everyday: AppsIcon,
  admin: AdminPanelSettingsIcon,
};

// Icon tile tint per section (literal strings so Tailwind keeps them).
const SECTION_TONES = {
  forms: "bg-sky-500/15 text-sky-600 dark:text-sky-300",
  "windows-doors": "bg-indigo-500/15 text-indigo-600 dark:text-indigo-300",
  sales: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
  cabinets: "bg-amber-500/15 text-amber-600 dark:text-amber-300",
  sbcf: "bg-orange-500/15 text-orange-600 dark:text-orange-300",
  fleet: "bg-teal-500/15 text-teal-600 dark:text-teal-300",
  safety: "bg-rose-500/15 text-rose-600 dark:text-rose-300",
  everyday: "bg-violet-500/15 text-violet-600 dark:text-violet-300",
  admin: "bg-slate-500/15 text-slate-600 dark:text-slate-300",
};

// Exact match or a deeper path under it — plain startsWith would keep
// "New Service Request" (/windowdoor/service-request) highlighted while
// you're on /windowdoor/service-requests.
const isActivePath = (pathname, path) =>
  pathname === path || pathname.startsWith(path + "/");

const OPEN_SECTIONS_KEY = "sideNavOpenSections";

const loadOpenSections = () => {
  try {
    return JSON.parse(localStorage.getItem(OPEN_SECTIONS_KEY)) || {};
  } catch {
    return {};
  }
};

const saveOpenSections = (state) => {
  try {
    localStorage.setItem(OPEN_SECTIONS_KEY, JSON.stringify(state));
  } catch {
    // Private browsing / storage disabled — open state just won't persist.
  }
};

const NavItem = ({ item, active, indent, onNavigate }) => {
  if (item.disabled) {
    // Announced but not launched: visible, not clickable.
    return (
      <div
        className={`flex items-center gap-2 py-2 pr-3 opacity-60 ${indent ? "pl-[3.25rem]" : "pl-3"}`}
      >
        <span className="text-sm">{item.name}</span>
        <span className="app-chip text-[10px] font-semibold uppercase tracking-wide">
          {item.note || "Coming soon"}
        </span>
      </div>
    );
  }

  return (
    <button
      onClick={() => onNavigate(item.path)}
      aria-current={active ? "page" : undefined}
      className={`relative flex w-full items-center rounded-xl py-2 pr-3 text-left text-sm transition-colors ${
        indent ? "pl-[3.25rem]" : "pl-3"
      } ${
        active
          ? "bg-sky-500/15 font-semibold text-sky-700 dark:text-sky-300"
          : "text-[color:var(--app-text)] hover:bg-[color:var(--app-hover)]"
      }`}
    >
      {active && indent && (
        <span
          aria-hidden="true"
          className="absolute left-[2.15rem] top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-sky-500"
        />
      )}
      {item.name}
    </button>
  );
};

const NavSection = ({ section, groups, open, onToggle, pathname, onNavigate }) => {
  const Icon = SECTION_ICONS[section.id] || AppsIcon;
  const tone = SECTION_TONES[section.id] || SECTION_TONES.everyday;

  return (
    <div className="py-0.5">
      <button
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-[color:var(--app-hover)]"
      >
        <span className={`app-icon-tile h-9 w-9 rounded-[0.7rem] ${tone}`}>
          <Icon sx={{ fontSize: 19 }} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold">{section.title}</span>
          <span className="app-muted block truncate text-xs">
            {section.description}
          </span>
        </span>
        <ExpandMoreIcon
          fontSize="small"
          className={`app-muted shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="pb-2 pt-0.5">
          {groups.map((group, gi) => (
            <div key={group.title || gi}>
              {group.title && (
                <div className="app-eyebrow pb-0.5 pl-[3.25rem] pr-3 pt-2">
                  {group.title}
                </div>
              )}
              {group.items.map((item) => (
                <NavItem
                  key={item.path}
                  item={item}
                  active={isActivePath(pathname, item.path)}
                  indent
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// A hub section (`hubPath` in components/Navigation/SideNav.js) is a single
// row that opens its hub page instead of expanding. It stays highlighted on
// the hub and on every page the hub lists, so you can tell where you are.
const NavHubRow = ({ section, active, current, onNavigate }) => {
  const Icon = SECTION_ICONS[section.id] || AppsIcon;
  const tone = SECTION_TONES[section.id] || SECTION_TONES.everyday;

  return (
    <div className="py-0.5">
      <button
        onClick={() => onNavigate(section.hubPath)}
        aria-current={current ? "page" : undefined}
        className={`flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors ${
          active ? "bg-sky-500/15" : "hover:bg-[color:var(--app-hover)]"
        }`}
      >
        <span className={`app-icon-tile h-9 w-9 rounded-[0.7rem] ${tone}`}>
          <Icon sx={{ fontSize: 19 }} />
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={`block text-sm font-semibold ${
              active ? "text-sky-700 dark:text-sky-300" : ""
            }`}
          >
            {section.title}
          </span>
          <span className="app-muted block truncate text-xs">
            {section.description}
          </span>
        </span>
        <ChevronRightIcon fontSize="small" className="app-muted shrink-0" />
      </button>
    </div>
  );
};

function SideNav({ setShowSidebar }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const role = useSelector(selectRoles);
  const userRoles = useMemo(() => role?.roles || [], [role]);
  const grantedPaths = role?.grantedPaths;

  const [query, setQuery] = useState("");
  // null = user hasn't touched that section this browser; fall back to
  // defaultOpen / contains-the-active-page.
  const [openSections, setOpenSections] = useState(loadOpenSections);

  const handleNavigate = (path) => {
    // The drawer stays mounted while closed, so clear the search too or the
    // next open would still be filtered by the old query.
    setQuery("");
    setShowSidebar(false);
    navigate(path);
  };

  // Role-filtered sections; a section only exists for you if it holds at
  // least one page you can open (so e.g. IT sees Cabinets just for
  // Cabinet Admin, and non-admins never see an empty Admin header).
  const visibleSections = useMemo(
    () =>
      NAV_SECTIONS.map((section) => ({
        section,
        groups: sectionGroups(section)
          .map((group) => ({
            ...group,
            items: group.items.filter((item) =>
              checkRoles(item.roles, userRoles, item.path, grantedPaths)
            ),
          }))
          .filter((group) => group.items.length > 0),
      })).filter(({ groups }) => groups.length > 0),
    [userRoles, grantedPaths]
  );

  // The browse tree hides navHidden items (they're reached through the Forms
  // hub); search below still includes them so every page stays findable.
  // Hub sections keep all their visible items: they render as one row, and
  // the items only decide whether that row shows and when it's highlighted.
  const browseSections = useMemo(
    () =>
      visibleSections
        .map(({ section, groups }) =>
          section.hubPath
            ? { section, groups }
            : {
                section,
                groups: groups
                  .map((group) => ({
                    ...group,
                    items: group.items.filter((item) => !item.navHidden),
                  }))
                  .filter((group) => group.items.length > 0),
              }
        )
        .filter(({ groups }) => groups.length > 0),
    [visibleSections]
  );

  const trimmedQuery = query.trim();

  // Search results: same visibility rules, flattened and grouped by section.
  const searchResults = useMemo(() => {
    if (!trimmedQuery) return null;
    return visibleSections
      .map(({ section, groups }) => ({
        section,
        items: groups.flatMap((group) =>
          group.items.filter((item) =>
            matchesQuery(
              [
                item.name,
                ...(item.keywords || []),
                section.title,
                group.title || "",
              ]
                .join(" ")
                .toLowerCase(),
              trimmedQuery
            )
          )
        ),
      }))
      .filter(({ items }) => items.length > 0);
  }, [visibleSections, trimmedQuery]);

  const isOpen = ({ section, groups }) => {
    const userChoice = openSections[section.id];
    if (typeof userChoice === "boolean") return userChoice;
    return (
      Boolean(section.defaultOpen) ||
      groups.some((group) =>
        group.items.some((item) => isActivePath(pathname, item.path))
      )
    );
  };

  const toggleSection = (entry) => {
    const next = { ...openSections, [entry.section.id]: !isOpen(entry) };
    setOpenSections(next);
    saveOpenSections(next);
  };

  return (
    <nav aria-label="Site navigation" className="pb-2">
      {/* Search */}
      <div className="px-1 pb-3 pt-1">
        <div className="flex items-center gap-2 rounded-full border border-[color:var(--app-input-border)] bg-[color:var(--app-input-bg)] px-3.5 py-2 transition focus-within:border-sky-500 focus-within:ring-[3px] focus-within:ring-[color:var(--app-ring)]">
          <SearchIcon fontSize="small" className="app-muted shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape" && query) {
                // Eat the Escape so it clears the search instead of closing
                // the drawer.
                e.stopPropagation();
                setQuery("");
              }
            }}
            placeholder="Find a page…"
            aria-label="Search navigation"
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-[color:var(--app-text)] outline-none placeholder:text-[color:var(--app-placeholder)] focus:ring-0"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="app-muted shrink-0 rounded-full p-0.5 hover:text-[color:var(--app-text)]"
            >
              <ClearIcon fontSize="small" />
            </button>
          )}
        </div>
      </div>

      {searchResults ? (
        searchResults.length === 0 ? (
          <div className="app-muted px-4 py-6 text-center text-sm">
            <p>No pages match “{trimmedQuery}”.</p>
            <button
              onClick={() => handleNavigate("/help")}
              className="app-link mt-2"
            >
              Search the Help &amp; FAQ instead
            </button>
          </div>
        ) : (
          <div className="space-y-1 px-1">
            {searchResults.map(({ section, items }) => (
              <div key={section.id}>
                <div className="app-eyebrow px-3 pb-0.5 pt-2">
                  {section.title}
                </div>
                {items.map((item) => (
                  <NavItem
                    key={item.path}
                    item={item}
                    active={isActivePath(pathname, item.path)}
                    onNavigate={handleNavigate}
                  />
                ))}
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="px-1">
          {browseSections.map((entry) =>
            entry.section.hubPath ? (
              <NavHubRow
                key={entry.section.id}
                section={entry.section}
                current={pathname === entry.section.hubPath}
                active={
                  isActivePath(pathname, entry.section.hubPath) ||
                  entry.groups.some((group) =>
                    group.items.some((item) => isActivePath(pathname, item.path))
                  )
                }
                onNavigate={handleNavigate}
              />
            ) : (
              <NavSection
                key={entry.section.id}
                section={entry.section}
                groups={entry.groups}
                open={isOpen(entry)}
                onToggle={() => toggleSection(entry)}
                pathname={pathname}
                onNavigate={handleNavigate}
              />
            )
          )}
        </div>
      )}
    </nav>
  );
}

export default SideNav;
